import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Globe, Check, ArrowRight, Loader2, Wand2, Search, Plus, Trash2 } from 'lucide-react';
import { useRemainingCredits } from '../../shared/providers/RemainingCreditsProvider';
import { TRANSLATION_LANGUAGES, searchTranslationLanguages } from '../../shared/constants/translationLanguages';
import supabase from '../../shared/supabase/supabase';

// Hook to detect mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

const EMOJI_OPTIONS = [
  '✨', '🔥', '💡', '🎨', '📝', '🚀',
  '💼', '📊', '🎬', '🎵', '📰', '💎',
  '🏆', '⭐', '🌟', '💫', '✅', '📌',
  '🔑', '💬', '🗣️', '📣', '🎤', '🧠',
  '💪', '🎯', '🔮', '🌈', '❤️', '☕',
];

interface CustomMode {
  id: string;
  title: string;
  emoji: string;
  prompt: string;
  created_at: string;
  user_id: string;
}

interface SlidingSidebarProps {
  isOpen: boolean;
  mode: 'rewrite' | 'translate' | null;
  onClose: () => void;
  editorContent: string;
  onRewrite?: (option: string) => void;
  onShowBilling?: () => void;
}

const SlidingSidebar: React.FC<SlidingSidebarProps> = ({
  isOpen,
  mode,
  onClose,
  editorContent,
  onRewrite,
  onShowBilling,
}) => {
  const { usage, limits } = useRemainingCredits();
  const isUnlimited = usage.isUnlimited || limits.isUnlimited || limits.isAppSumo;
  const isMobile = useIsMobile();

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const [languageSearchQuery, setLanguageSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Custom modes state
  const [customModes, setCustomModes] = useState<CustomMode[]>([]);
  const [hideDefaults, setHideDefaults] = useState(() => {
    return localStorage.getItem('hideDefaultRewriteModes') === 'true';
  });
  const [showNewModeWizard, setShowNewModeWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [newModePrompt, setNewModePrompt] = useState('');
  const [newModeTitle, setNewModeTitle] = useState('');
  const [newModeEmoji, setNewModeEmoji] = useState('');
  const [isSavingMode, setIsSavingMode] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Load saved custom instruction from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('customRewriteInstruction');
    if (saved) {
      setCustomInstruction(saved);
    }
  }, []);

  // Fetch custom modes from Supabase
  const fetchCustomModes = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('CustomRewriteMode')
        .select('*')
        .order('created_at', { ascending: false });
      if (data) setCustomModes(data as CustomMode[]);
    } catch {
      // Silently fail — modes just won't show
    }
  }, []);

  useEffect(() => {
    if (isOpen && mode === 'rewrite') {
      fetchCustomModes();
    }
  }, [isOpen, mode, fetchCustomModes]);

  // Auto-dismiss delete confirmation after 4s
  useEffect(() => {
    if (confirmDeleteId) {
      const timer = setTimeout(() => setConfirmDeleteId(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [confirmDeleteId]);

  const rewriteOptions = [
    // Custom
    { category: 'Custom', id: 'custom', title: 'Custom Instructions', description: 'Tell AI exactly what you want', icon: '🎯', isPro: true },

    // General
    { category: 'General', id: 'auto-enhance', title: 'Auto-Enhance', description: 'AI picks the best improvements', icon: '✨', isPro: false },
    { category: 'General', id: 'quick-polish', title: 'Quick Polish', description: 'Minor fixes and cleanup', icon: '💎', isPro: true },
    { category: 'General', id: 'deep-rewrite', title: 'Deep Rewrite', description: 'Complete revision for clarity', icon: '🔄', isPro: true },
    { category: 'General', id: 'restructure', title: 'Restructure', description: 'Organize with headings and bullets', icon: '🏗️', isPro: true },
    { category: 'General', id: 'make-concise', title: 'Make Concise', description: 'Shorten while keeping meaning', icon: '🎯', isPro: true },
    { category: 'General', id: 'create-list', title: 'Create List', description: 'Convert to actionable items', icon: '✅', isPro: true },

    // Content Creation
    { category: 'Content Creation', id: 'tweet', title: 'Tweet', description: 'Single engaging post', icon: '🐦', isPro: true },
    { category: 'Content Creation', id: 'twitter-thread', title: 'Twitter Thread', description: 'Multi-part story', icon: '🧵', isPro: true },
    { category: 'Content Creation', id: 'facebook', title: 'Facebook', description: 'Engaging social post', icon: '👥', isPro: true },
    { category: 'Content Creation', id: 'linkedin', title: 'LinkedIn', description: 'Professional update', icon: '💼', isPro: true },
    { category: 'Content Creation', id: 'instagram', title: 'Instagram', description: 'Caption with hashtags', icon: '📸', isPro: true },
    { category: 'Content Creation', id: 'youtube-script', title: 'YouTube Script', description: 'Full video script', icon: '🎬', isPro: true },
    { category: 'Content Creation', id: 'tiktok-script', title: 'TikTok Script', description: 'Short-form video', icon: '🎵', isPro: true },
    { category: 'Content Creation', id: 'newsletter', title: 'Newsletter', description: 'Email content', icon: '📰', isPro: true },

    // Professional
    { category: 'Professional', id: 'email', title: 'Email', description: 'Professional business email', icon: '📧', isPro: true },
    { category: 'Professional', id: 'meeting-notes', title: 'Meeting Notes', description: 'Organized with action items', icon: '📝', isPro: true },
    { category: 'Professional', id: 'create-outline', title: 'Create Outline', description: 'Document framework', icon: '📑', isPro: true },
    { category: 'Professional', id: 'prd', title: 'PRD', description: 'Product requirements document', icon: '📋', isPro: true },
    { category: 'Professional', id: 'proposal', title: 'Proposal', description: 'Business proposal format', icon: '💡', isPro: true },
    { category: 'Professional', id: 'pitch-deck', title: 'Pitch Deck', description: 'Presentation outline', icon: '🚀', isPro: true },
    { category: 'Professional', id: 'report', title: 'Report', description: 'Executive summary style', icon: '📊', isPro: true },

    // Writing Styles
    { category: 'Writing Styles', id: 'business-tone', title: 'Business Tone', description: 'Corporate voice', icon: '🏢', isPro: true },
    { category: 'Writing Styles', id: 'formal', title: 'Formal', description: 'Official documents', icon: '🎩', isPro: true },
    { category: 'Writing Styles', id: 'casual', title: 'Casual', description: 'Relaxed and friendly', icon: '☕', isPro: true },
    { category: 'Writing Styles', id: 'friendly', title: 'Friendly', description: 'Warm and personal', icon: '🤝', isPro: true },
    { category: 'Writing Styles', id: 'direct-clear', title: 'Direct & Clear', description: 'No unnecessary words', icon: '⚡', isPro: true },

    // Journaling
    { category: 'Journaling', id: 'daily-entry', title: 'Daily Entry', description: 'Organize your thoughts', icon: '📖', isPro: true },
  ];

  // Use the full list of translation languages (99+ supported by OpenAI)
  const languages = TRANSLATION_LANGUAGES;

  // Use the searchTranslationLanguages helper function
  const filteredLanguages = useMemo(() => {
    return searchTranslationLanguages(languageSearchQuery);
  }, [languageSearchQuery]);

  const handleOptionClick = (optionId: string) => {
    // Find the option to check if it's a MAX feature
    const rewriteOption = rewriteOptions.find(opt => opt.id === optionId);
    const languageOption = languages.find(lang => lang.code === optionId);

    // Check if it's a MAX feature (only rewrite options have isPro property)
    const isMaxFeature = rewriteOption?.isPro === true;

    // If it's a MAX feature and user doesn't have unlimited, show billing modal
    // Don't close sidebar in this case
    if (isMaxFeature && !isUnlimited) {
      if (onShowBilling) {
        onShowBilling();
      }
      return;
    }

    if (optionId === 'custom') {
      setShowCustomModal(true);
      return;
    }

    setSelectedOption(optionId);
    setIsProcessing(true);

    // Close immediately on mobile (or always if you prefer)
    if (isMobile) {
      onClose();
    }

    // Trigger the rewrite/translate immediately
    if (onRewrite) {
      // For language translations, prefix with translate_
      const featureId = languageOption ? `translate_${optionId}` : optionId;
      onRewrite(featureId);
    }

    // Reset processing state after a delay
    setTimeout(() => {
      setIsProcessing(false);
    }, 2000);
  };

  const handleCustomInstructionSubmit = () => {
    // Save to localStorage
    localStorage.setItem('customRewriteInstruction', customInstruction);
    setShowCustomModal(false);
    setSelectedOption('custom');
    setIsProcessing(true);

    // Close immediately on mobile
    if (isMobile) {
      onClose();
    }

    // Trigger the rewrite with custom instruction
    if (onRewrite) {
      onRewrite(`custom:${customInstruction}`);
    }

    // Reset processing state after a delay
    setTimeout(() => {
      setIsProcessing(false);
    }, 2000);
  };

  // --- Custom mode handlers ---

  const handleToggleHideDefaults = () => {
    const next = !hideDefaults;
    setHideDefaults(next);
    localStorage.setItem('hideDefaultRewriteModes', String(next));
  };

  const handleCustomModeClick = (customMode: CustomMode) => {
    if (!isUnlimited) {
      if (onShowBilling) onShowBilling();
      return;
    }

    setSelectedOption(`custom-mode-${customMode.id}`);
    setIsProcessing(true);

    if (isMobile) onClose();

    if (onRewrite) {
      onRewrite(`custom:${customMode.prompt}`);
    }

    setTimeout(() => setIsProcessing(false), 2000);
  };

  const handleNewModeClick = () => {
    if (!isUnlimited) {
      if (onShowBilling) onShowBilling();
      return;
    }
    setShowNewModeWizard(true);
    setWizardStep(1);
    setNewModePrompt('');
    setNewModeTitle('');
    setNewModeEmoji('');
  };

  const handleCreateMode = async () => {
    setIsSavingMode(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('CustomRewriteMode')
        .insert({
          user_id: user.id,
          title: newModeTitle.trim(),
          emoji: newModeEmoji,
          prompt: newModePrompt.trim(),
        });

      if (!error) {
        await fetchCustomModes();
        setShowNewModeWizard(false);
      }
    } finally {
      setIsSavingMode(false);
    }
  };

  const handleDeleteMode = async (id: string) => {
    await supabase.from('CustomRewriteMode').delete().eq('id', id);
    setCustomModes(prev => prev.filter(m => m.id !== id));
    setConfirmDeleteId(null);
  };

  const canContinue =
    wizardStep === 1 ? newModePrompt.trim().length > 0 :
    wizardStep === 2 ? newModeTitle.trim().length > 0 :
    newModeEmoji.length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - No blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/10 z-40"
          />

          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white shadow-2xl z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="relative px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {mode === 'rewrite' ? (
                    <>
                      <motion.div
                        animate={{ rotate: [0, 180, 360] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center"
                      >
                        <Sparkles className="w-4 h-4 text-purple-600" strokeWidth={2} />
                      </motion.div>
                      <div>
                        <h2 className="text-base sm:text-lg font-medium text-gray-900">Rewrite Options</h2>
                        <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">Choose how to transform your text</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                        <Globe className="w-4 h-4 text-blue-600" strokeWidth={2} />
                      </div>
                      <div>
                        <h2 className="text-base sm:text-lg font-medium text-gray-900">Translate</h2>
                        <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">Choose from 120+ languages</p>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  {/* Hide defaults toggle */}
                  {mode === 'rewrite' && customModes.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center space-x-2"
                    >
                      <span className="text-[10px] text-gray-400 hidden sm:block">Defaults</span>
                      <button
                        onClick={handleToggleHideDefaults}
                        className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                          hideDefaults ? 'bg-gray-300' : 'bg-purple-500'
                        }`}
                      >
                        <motion.div
                          animate={{ x: hideDefaults ? 0 : 16 }}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm"
                        />
                      </button>
                    </motion.div>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" strokeWidth={2} />
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="h-full overflow-y-auto pb-24 sm:pb-40">
              {mode === 'rewrite' ? (
                <div className="p-4 sm:p-6 pb-24 sm:pb-32">
                  {/* My Modes section */}
                  {customModes.length > 0 && (
                    <div className="mb-6">
                      <motion.h3
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-xs font-semibold text-amber-500 uppercase tracking-wider mb-3 px-1 flex items-center space-x-1.5"
                      >
                        <span>My Modes</span>
                      </motion.h3>

                      <div className="space-y-2">
                        <AnimatePresence>
                          {customModes.map((customMode, index) => (
                            <motion.div
                              key={customMode.id}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                              transition={{ delay: index * 0.03 }}
                              layout
                            >
                              {confirmDeleteId === customMode.id ? (
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.95 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  className="p-2.5 sm:p-3 rounded-xl border border-red-200 bg-red-50"
                                >
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs sm:text-sm text-red-600 font-medium">Delete &ldquo;{customMode.title}&rdquo;?</p>
                                    <div className="flex items-center space-x-2">
                                      <button
                                        onClick={() => setConfirmDeleteId(null)}
                                        className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-white transition-colors"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => handleDeleteMode(customMode.id)}
                                        className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded-lg hover:bg-red-100 transition-colors"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </motion.div>
                              ) : (
                                <button
                                  onClick={() => handleCustomModeClick(customMode)}
                                  disabled={isProcessing}
                                  className={`w-full p-2.5 sm:p-3 rounded-xl border transition-all ${
                                    selectedOption === `custom-mode-${customMode.id}`
                                      ? 'border-amber-200 bg-amber-50'
                                      : 'border-gray-100 hover:border-gray-200 bg-white hover:bg-gray-50'
                                  } group relative overflow-hidden`}
                                >
                                  <div className="flex items-center space-x-2 sm:space-x-3">
                                    <div className="text-lg sm:text-xl flex-shrink-0">{customMode.emoji}</div>
                                    <div className="flex-1 text-left min-w-0">
                                      <div className="flex items-center space-x-1.5 sm:space-x-2">
                                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 truncate">{customMode.title}</h3>
                                        <span className="text-[9px] sm:text-[10px] font-semibold bg-gradient-to-r from-amber-400 to-amber-500 text-white px-1 sm:px-1.5 py-0.5 rounded flex-shrink-0">
                                          MY
                                        </span>
                                      </div>
                                      <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 truncate hidden sm:block">{customMode.prompt}</p>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <motion.button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setConfirmDeleteId(customMode.id);
                                        }}
                                        className="w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all"
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                      >
                                        <Trash2 className="w-3 h-3 text-gray-400 group-hover:text-red-400" strokeWidth={2} />
                                      </motion.button>
                                      <motion.div
                                        animate={{
                                          x: selectedOption === `custom-mode-${customMode.id}` ? 0 : 10,
                                          opacity: selectedOption === `custom-mode-${customMode.id}` ? 1 : 0,
                                        }}
                                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                      >
                                        {isProcessing && selectedOption === `custom-mode-${customMode.id}` ? (
                                          <Loader2 className="w-4 h-4 text-amber-500 animate-spin" strokeWidth={2} />
                                        ) : selectedOption === `custom-mode-${customMode.id}` ? (
                                          <Check className="w-4 h-4 text-amber-500" strokeWidth={2} />
                                        ) : null}
                                      </motion.div>
                                    </div>
                                  </div>

                                  {/* Animated gradient background on selection */}
                                  <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-500 opacity-5 -z-10"
                                    initial={{ scale: 0, borderRadius: '100%' }}
                                    animate={{
                                      scale: selectedOption === `custom-mode-${customMode.id}` ? 2 : 0,
                                      borderRadius: selectedOption === `custom-mode-${customMode.id}` ? '0%' : '100%',
                                    }}
                                    transition={{ duration: 0.3 }}
                                  />
                                </button>
                              )}
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    </div>
                  )}

                  {/* Default categories */}
                  <AnimatePresence>
                    {!hideDefaults && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {/* Group options by category */}
                        {Object.entries(
                          rewriteOptions.reduce((acc, option) => {
                            if (!acc[option.category]) acc[option.category] = [];
                            acc[option.category].push(option);
                            return acc;
                          }, {} as Record<string, typeof rewriteOptions>)
                        ).map(([category, options], categoryIndex) => (
                          <div key={category} className="mb-6">
                            {/* Category Header */}
                            <motion.h3
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: categoryIndex * 0.05 }}
                              className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1"
                            >
                              {category}
                            </motion.h3>

                            {/* Options in category */}
                            <div className="space-y-2">
                              {options.map((option, index) => (
                                <motion.button
                                  key={option.id}
                                  initial={{ opacity: 0, x: 20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: categoryIndex * 0.05 + index * 0.02 }}
                                  onClick={() => handleOptionClick(option.id)}
                                  disabled={isProcessing}
                                  className={`w-full p-2.5 sm:p-3 rounded-xl border transition-all ${
                                    selectedOption === option.id
                                      ? 'border-purple-200 bg-purple-50'
                                      : 'border-gray-100 hover:border-gray-200 bg-white hover:bg-gray-50'
                                  } group relative overflow-hidden`}
                                >
                                  <div className="flex items-center space-x-2 sm:space-x-3">
                                    <div className="text-lg sm:text-xl flex-shrink-0">{option.icon}</div>
                                    <div className="flex-1 text-left min-w-0">
                                      <div className="flex items-center space-x-1.5 sm:space-x-2">
                                        <h3 className="text-xs sm:text-sm font-medium text-gray-900 truncate">{option.title}</h3>
                                        {option.isPro && (
                                          <span className="text-[9px] sm:text-[10px] font-semibold bg-gradient-to-r from-purple-600 to-purple-700 text-white px-1 sm:px-1.5 py-0.5 rounded flex-shrink-0">
                                            MAX
                                          </span>
                                        )}
                                      </div>
                                      {option.description && (
                                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 hidden sm:block">{option.description}</p>
                                      )}
                                    </div>
                                    <motion.div
                                      animate={{
                                        x: selectedOption === option.id ? 0 : 10,
                                        opacity: selectedOption === option.id ? 1 : 0,
                                      }}
                                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                    >
                                      {isProcessing && selectedOption === option.id ? (
                                        <Loader2 className="w-4 h-4 text-purple-600 animate-spin" strokeWidth={2} />
                                      ) : selectedOption === option.id ? (
                                        <Check className="w-4 h-4 text-purple-600" strokeWidth={2} />
                                      ) : (
                                        <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
                                      )}
                                    </motion.div>
                                  </div>

                                  {/* Animated gradient background on selection */}
                                  <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-purple-500 to-purple-600 opacity-5 -z-10"
                                    initial={{ scale: 0, borderRadius: '100%' }}
                                    animate={{
                                      scale: selectedOption === option.id ? 2 : 0,
                                      borderRadius: selectedOption === option.id ? '0%' : '100%',
                                    }}
                                    transition={{ duration: 0.3 }}
                                  />
                                </motion.button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Empty state when only My Modes is visible and it's empty */}
                  {hideDefaults && customModes.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex flex-col items-center justify-center py-16"
                    >
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                        <Plus className="w-5 h-5 text-gray-400" strokeWidth={2} />
                      </div>
                      <p className="text-sm text-gray-400">No custom modes yet</p>
                      <p className="text-xs text-gray-400 mt-1">Create one below</p>
                    </motion.div>
                  )}
                </div>
              ) : (
                <div className="pb-24 sm:pb-32">
                  {/* Search Bar for Languages */}
                  <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 sm:pb-6">
                    <div className="relative group">
                      <motion.div
                        animate={{
                          scale: isSearchFocused ? 1.01 : 1,
                        }}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 25,
                        }}
                        className="relative"
                      >
                        {/* Search Icon */}
                        <motion.div
                          animate={{
                            x: isSearchFocused ? 4 : 0,
                            opacity: isSearchFocused ? 0.7 : 1,
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 200,
                            damping: 20,
                          }}
                          className="absolute left-4 inset-y-0 flex items-center pointer-events-none"
                        >
                          <Search className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                        </motion.div>

                        {/* Input Field */}
                        <input
                          type="text"
                          value={languageSearchQuery}
                          onChange={(e) => setLanguageSearchQuery(e.target.value)}
                          onFocus={() => setIsSearchFocused(true)}
                          onBlur={() => setIsSearchFocused(false)}
                          placeholder="Search languages..."
                          className="w-full pl-11 pr-10 py-3 bg-white rounded-xl border border-gray-200
                                   focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10
                                   text-sm text-gray-900 placeholder:text-gray-400
                                   transition-all duration-200"
                          style={{
                            boxShadow: isSearchFocused
                              ? "0 10px 40px -10px rgba(14, 165, 233, 0.15), 0 0 0 1px rgba(14, 165, 233, 0.1)"
                              : "0 2px 8px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.02)",
                          }}
                        />

                        {/* Clear Button */}
                        <AnimatePresence>
                          {languageSearchQuery && (
                            <motion.button
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => setLanguageSearchQuery("")}
                              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5
                                       flex items-center justify-center rounded-full
                                       bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              <X className="w-3 h-3 text-gray-600" strokeWidth={2} />
                            </motion.button>
                          )}
                        </AnimatePresence>

                        {/* Search Results Count */}
                        <AnimatePresence>
                          {languageSearchQuery && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute -bottom-5 left-0 text-xs text-gray-500"
                            >
                              {filteredLanguages.length === 0 ? (
                                <span>No languages found</span>
                              ) : (
                                <span>
                                  {filteredLanguages.length} language
                                  {filteredLanguages.length !== 1 ? "s" : ""}
                                </span>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>

                      {/* Subtle gradient line under search when focused */}
                      <motion.div
                        className="absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-500 to-transparent"
                        initial={{ scaleX: 0, opacity: 0 }}
                        animate={{
                          scaleX: isSearchFocused ? 1 : 0,
                          opacity: isSearchFocused ? 0.5 : 0,
                        }}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 25,
                        }}
                      />
                    </div>
                  </div>

                  {/* Language Grid */}
                  <AnimatePresence mode="popLayout">
                    {filteredLanguages.length === 0 && languageSearchQuery ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col items-center justify-center py-20"
                      >
                        <motion.div
                          animate={{
                            rotate: [0, -5, 5, -5, 0],
                            scale: [1, 1.05, 1, 1.05, 1],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatDelay: 3,
                          }}
                          className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4"
                        >
                          <Search className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
                        </motion.div>
                        <p className="text-gray-500 text-sm">
                          No languages match your search
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                          Try different keywords
                        </p>
                      </motion.div>
                    ) : (
                      <motion.div
                        className="px-4 sm:px-6 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5 mt-2"
                        layout
                      >
                        <AnimatePresence>
                          {filteredLanguages.map((language, index) => (
                            <motion.div
                              key={language.code}
                              layout
                              initial={{ opacity: 0, scale: 0.9, y: 20 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.9, y: -20 }}
                              transition={{
                                layout: {
                                  type: "spring",
                                  stiffness: 300,
                                  damping: 30,
                                },
                                opacity: {
                                  duration: 0.2,
                                },
                                scale: {
                                  type: "spring",
                                  stiffness: 200,
                                  damping: 20,
                                  delay: index * 0.02,
                                },
                              }}
                            >
                              <button
                                onClick={() => handleOptionClick(language.code)}
                                disabled={isProcessing}
                                className={`relative w-full p-2.5 sm:p-3 rounded-xl border transition-all hover:scale-[1.02] active:scale-[0.98] ${
                                  selectedOption === language.code
                                    ? 'border-blue-200 bg-blue-50 shadow-lg'
                                    : 'border-gray-100 hover:border-gray-200 bg-white hover:bg-gray-50 hover:shadow-md'
                                } group overflow-hidden`}
                                style={{
                                  boxShadow: selectedOption === language.code
                                    ? "0 10px 40px -10px rgba(59, 130, 246, 0.2), 0 0 0 1px rgba(59, 130, 246, 0.1)"
                                    : undefined
                                }}
                              >
                                <div className="flex flex-col items-center space-y-1">
                                  <motion.div
                                    className="text-xl sm:text-2xl"
                                    animate={{
                                      scale: selectedOption === language.code ? [1, 1.1, 1] : 1,
                                    }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    {language.flag}
                                  </motion.div>
                                  <div className="text-center w-full">
                                    <p className="text-[11px] sm:text-xs font-medium text-gray-900 truncate max-w-full px-1 leading-tight">
                                      {language.name.includes('(') ? language.name.split('(')[0].trim() : language.name}
                                    </p>
                                    {language.name.includes('(') && (
                                      <p className="text-[9px] sm:text-[10px] text-gray-600 truncate max-w-full px-1 leading-tight">
                                        ({language.name.split('(')[1].replace(')', '').trim()})
                                      </p>
                                    )}
                                    <p className="text-[9px] sm:text-[10px] text-gray-500 truncate max-w-full px-1 leading-tight mt-0.5">{language.native}</p>
                                  </div>
                                  {isProcessing && selectedOption === language.code && (
                                    <motion.div
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      className="absolute top-2 right-2"
                                    >
                                      <Loader2 className="w-3 h-3 text-blue-600 animate-spin" strokeWidth={2} />
                                    </motion.div>
                                  )}
                                </div>

                                {/* Animated gradient background on selection */}
                                <motion.div
                                  className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 opacity-5 -z-10"
                                  initial={{ scale: 0, borderRadius: '100%' }}
                                  animate={{
                                    scale: selectedOption === language.code ? 2 : 0,
                                    borderRadius: selectedOption === language.code ? '0%' : '100%',
                                  }}
                                  transition={{ duration: 0.3 }}
                                />
                              </button>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* New Mode Button - Fixed at bottom */}
            {mode === 'rewrite' && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 25 }}
                className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-t from-white via-white to-transparent pt-10"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleNewModeClick}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium text-sm flex items-center justify-center space-x-2 shadow-lg shadow-purple-500/25 hover:from-purple-600 hover:to-purple-700 transition-all"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} />
                  <span>New Mode</span>
                </motion.button>
              </motion.div>
            )}
          </motion.div>

          {/* Custom Instruction Modal */}
          <AnimatePresence>
            {showCustomModal && (
              <>
                {/* Modal Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowCustomModal(false)}
                  className="fixed inset-0 bg-black/30 z-[60]"
                />

                {/* Modal Content */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 25,
                  }}
                  className="fixed inset-0 flex items-center justify-center z-[61] px-4 sm:px-0"
                >
                  <div
                    className="w-full max-w-lg bg-white rounded-2xl sm:rounded-3xl shadow-2xl mx-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                  {/* Modal Header */}
                  <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <motion.div
                          animate={{ rotate: [0, 15, -15, 0] }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatDelay: 3,
                          }}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center"
                        >
                          <Wand2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" strokeWidth={2} />
                        </motion.div>
                        <div>
                          <h2 className="text-base sm:text-lg font-medium text-gray-900">Custom Instruction</h2>
                          <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">Tell AI exactly how to rewrite</p>
                        </div>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setShowCustomModal(false)}
                        className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-500" strokeWidth={2} />
                      </motion.button>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="px-4 sm:px-8 py-4 sm:py-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your instruction to AI
                    </label>
                    <textarea
                      value={customInstruction}
                      onChange={(e) => setCustomInstruction(e.target.value)}
                      placeholder="e.g., Make it more persuasive and add a call-to-action at the end..."
                      className="w-full h-28 sm:h-32 px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl sm:rounded-2xl resize-none
                               focus:border-purple-500 focus:outline-none focus:ring-4 focus:ring-purple-500/10
                               text-xs sm:text-sm text-gray-900 placeholder:text-gray-400
                               transition-all duration-200"
                      style={{
                        boxShadow: customInstruction
                          ? '0 4px 20px -4px rgba(147, 51, 234, 0.1)'
                          : 'none'
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Tip: Be specific about tone, style, length, or any special requirements
                    </p>
                  </div>

                  {/* Modal Footer */}
                  <div className="px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-100 flex items-center justify-between">
                    <button
                      onClick={() => setShowCustomModal(false)}
                      className="px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleCustomInstructionSubmit}
                      disabled={!customInstruction.trim()}
                      className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-white transition-all ${
                        customInstruction.trim()
                          ? 'bg-gradient-to-r from-purple-500 to-purple-600 shadow-lg hover:from-purple-600 hover:to-purple-700'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Apply Instruction
                    </motion.button>
                  </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* New Mode Wizard */}
          <AnimatePresence>
            {showNewModeWizard && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowNewModeWizard(false)}
                  className="fixed inset-0 bg-black/30 z-[60]"
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  className="fixed inset-0 flex items-center justify-center z-[61] px-4 sm:px-0"
                >
                  <div
                    className="w-full max-w-lg bg-white rounded-2xl sm:rounded-3xl shadow-2xl mx-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Wizard Header */}
                    <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <motion.div
                            animate={{ rotate: [0, 15, -15, 0] }}
                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center"
                          >
                            <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" strokeWidth={2} />
                          </motion.div>
                          <div>
                            <h2 className="text-base sm:text-lg font-medium text-gray-900">Create Rewrite Mode</h2>
                            <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">Save a custom instruction for later</p>
                          </div>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: 90 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setShowNewModeWizard(false)}
                          className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                        >
                          <X className="w-4 h-4 text-gray-500" strokeWidth={2} />
                        </motion.button>
                      </div>
                      {/* Step dots */}
                      <div className="flex items-center justify-center space-x-2 mt-4">
                        {[1, 2, 3].map((step) => (
                          <motion.div
                            key={step}
                            animate={{
                              scale: wizardStep === step ? 1.3 : 1,
                              backgroundColor: wizardStep >= step ? '#9333EA' : '#E5E7EB',
                            }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                            className="w-2 h-2 rounded-full"
                          />
                        ))}
                      </div>
                    </div>

                    {/* Step Content */}
                    <div className="min-h-[220px]">
                      <AnimatePresence mode="wait">
                        {wizardStep === 1 && (
                          <motion.div
                            key="step-1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.15 }}
                            className="px-4 sm:px-8 py-4 sm:py-6"
                          >
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              What should AI do?
                            </label>
                            <textarea
                              value={newModePrompt}
                              onChange={(e) => setNewModePrompt(e.target.value)}
                              placeholder="e.g., Rewrite in a friendly customer support tone with empathy..."
                              className="w-full h-28 sm:h-32 px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl sm:rounded-2xl resize-none
                                       focus:border-purple-500 focus:outline-none focus:ring-4 focus:ring-purple-500/10
                                       text-xs sm:text-sm text-gray-900 placeholder:text-gray-400
                                       transition-all duration-200"
                              autoFocus
                              style={{
                                boxShadow: newModePrompt
                                  ? '0 4px 20px -4px rgba(147, 51, 234, 0.1)'
                                  : 'none'
                              }}
                            />
                            <p className="text-xs text-gray-500 mt-2">
                              💡 Be specific about tone, style, length, or format
                            </p>
                          </motion.div>
                        )}
                        {wizardStep === 2 && (
                          <motion.div
                            key="step-2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.15 }}
                            className="px-4 sm:px-8 py-4 sm:py-6"
                          >
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Name this mode
                            </label>
                            <input
                              type="text"
                              value={newModeTitle}
                              onChange={(e) => setNewModeTitle(e.target.value)}
                              placeholder="e.g., Friendly Support, Blog Post, Sales Pitch..."
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl sm:rounded-2xl
                                       focus:border-purple-500 focus:outline-none focus:ring-4 focus:ring-purple-500/10
                                       text-xs sm:text-sm text-gray-900 placeholder:text-gray-400
                                       transition-all duration-200"
                              autoFocus
                              maxLength={40}
                              style={{
                                boxShadow: newModeTitle
                                  ? '0 4px 20px -4px rgba(147, 51, 234, 0.1)'
                                  : 'none'
                              }}
                            />
                            <p className="text-xs text-gray-400 mt-2 text-right">{newModeTitle.length}/40</p>
                          </motion.div>
                        )}
                        {wizardStep === 3 && (
                          <motion.div
                            key="step-3"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.15 }}
                            className="px-4 sm:px-8 py-4 sm:py-6"
                          >
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                              Pick an emoji
                            </label>
                            <div className="grid grid-cols-6 gap-2">
                              {EMOJI_OPTIONS.map((emoji) => (
                                <motion.button
                                  key={emoji}
                                  whileHover={{ scale: 1.15 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => setNewModeEmoji(emoji)}
                                  className={`p-2 sm:p-2.5 rounded-xl text-xl sm:text-2xl transition-all ${
                                    newModeEmoji === emoji
                                      ? 'bg-purple-100 border-2 border-purple-400 shadow-md'
                                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                                  }`}
                                >
                                  {emoji}
                                </motion.button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Wizard Footer */}
                    <div className="px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-100 flex items-center justify-between">
                      <button
                        onClick={() => {
                          if (wizardStep === 1) {
                            setShowNewModeWizard(false);
                          } else {
                            setWizardStep(s => s - 1);
                          }
                        }}
                        className="px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-colors"
                      >
                        {wizardStep === 1 ? 'Cancel' : 'Back'}
                      </button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          if (wizardStep === 3) {
                            handleCreateMode();
                          } else {
                            setWizardStep(s => s + 1);
                          }
                        }}
                        disabled={!canContinue || isSavingMode}
                        className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium text-white transition-all flex items-center space-x-2 ${
                          canContinue && !isSavingMode
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 shadow-lg hover:from-purple-600 hover:to-purple-700'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {isSavingMode ? (
                          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                        ) : (
                          <span>{wizardStep === 3 ? 'Create Mode' : 'Continue'}</span>
                        )}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
};

export default SlidingSidebar;
